import * as React from 'react';

import { Button, Badge, EntityDetails } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
  $agentDetailForm,
  $isCreateMode,
  $saveAgentDetail,
  $remoteAgent,
  $tab,
  type AgentDetailsTab,
} from '../../_state/agentDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { adminRoutes } from '../../routes';

import { AgentTabDetails } from './AgentTabDetails';
import { AgentTabPrompt } from './AgentTabPrompt';
import { AgentTabTools } from './AgentTabTools';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

export const AgentDetailView: React.FC = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  const isCreate = useAtomValue($isCreateMode);
  const remoteAgent = useAtomValue($remoteAgent);
  const formData = useAtomValue($agentDetailForm);
  const save = useSetAtom($saveAgentDetail);

  const [tab, setTab] = useAtom($tab);
  const activeTab = (tab || 'configuration') as AgentDetailsTab;

  // Form automatically syncs with agent data through $agentDetailForm atom

  const handleCancel = React.useCallback(() => {
    navigate(adminRoutes.aiAgents);
  }, [navigate]);

  const handleSave = React.useCallback(async () => {
    await save();
    navigate(adminRoutes.aiAgents);
  }, [navigate, save]);

  const statusBadge = React.useMemo(() => {
    if (isCreate) return null;
    return (
      <Badge color={formData.isActive ? 'success' : 'neutral'} size="small">
        {formData.isActive
          ? t.aiManagement.agents.status.active
          : t.aiManagement.agents.status.inactive}
      </Badge>
    );
  }, [isCreate, formData.isActive, t]);

  const subtitle =
    !isCreate && remoteAgent.data ? `v${remoteAgent.data.version}` : undefined;

  if (!isCreate && remoteAgent.isLoading) {
    return (
      <div className="@AgentDetailView flex h-full items-center justify-center">
        <div className="text-neutral/60">{t.aiManagement.loading.agent}</div>
      </div>
    );
  }

  if (!isCreate && remoteAgent.error) {
    return (
      <div className="@AgentDetailView flex h-full items-center justify-center">
        <div className="text-failure">
          {remoteAgent.error.message || t.aiManagement.errors.agentNotFound}
        </div>
      </div>
    );
  }

  return (
    <div className="@AgentDetailView flex grow flex-col">
      <EntityDetails
        className="@AgentDetailsView h-full"
        onBackPressed={handleCancel}
        primaryTitle={formData.name}
        subtitle={subtitle}
        badges={statusBadge}
        tabs={{
          configuration: {
            title: t.aiManagement.agents.details.tabs.configuration,
            content: AgentTabDetails,
          },
          prompt: {
            title: t.aiManagement.agents.tabs.prompt,
            content: AgentTabPrompt,
          },
          tools: {
            title: t.aiManagement.agents.tabs.tools,
            content: AgentTabTools,
          },
        }}
        currentTab={activeTab}
        onChangeTab={(t) => setTab(t as AgentDetailsTab)}
      />

      <div className="sticky bottom-0 border-t border-neutral/10 bg-space/95 p-4 backdrop-blur supports-[backdrop-filter]:bg-space/60">
        <div className="mx-auto flex max-w-4xl justify-end gap-2">
          <Button variant="outline" color="neutral" onClick={handleCancel}>
            {t.aiManagement.agents.actions.cancel}
          </Button>
          <Button variant="solid" color="primary" onClick={handleSave}>
            {t.aiManagement.agents.actions.save}
          </Button>
        </div>
      </div>
    </div>
  );
};
